<template>
  <div
    class="newsletter"
    :class="{ 'in-content': !featured }"
  >
    <UiComponentTag
      v-if="!featured"
      type="secondary"
    >
      <slot name="tag" />
    </UiComponentTag>
    <UiComponentHeading
      :as="as"
      :level="2"
    >
      <slot name="title" />
    </UiComponentHeading>
    <UiComponentImage
      v-if="icon"
      class="icon"
      :alt="icon.alt"
      :expected-size="iconSize"
      :height="icon.height"
      :source="icon.url"
      :width="icon.width"
    />
    <OrganismHubspotNativeForm
      :form-id="formId"
      unique-id="form-newsletter"
      @hubspot:form:ready="onReady"
      @hubspot:form:submitted="onSubmitted"
    />
  </div>
</template>

<script setup>
// Composables
const { locale } = useI18n()
const { width: screenWidth } = useScreenSize()

// Emitter
const emit = defineEmits([
  'newsletter:ready',
  'newsletter:subscribe',
])

// Props
const props = defineProps({
  as: {
    default() {
      return 'heading'
    },
    required: false,
    type: String,
    validator(value) {
      return ['heading', 'paragraph'].indexOf(value) !== -1
    },
  },
  featured: {
    default() {
      return false
    },
    type: Boolean,
  },
  icon: {
    default() {
      return {}
    },
    type: Object,
  },
})

// Computed Properties
const formId = computed(() => {
  switch (locale.value) {
    case 'de':
      return 'e34d91c8-00bf-466c-a975-e708bee4a284'
    case 'es':
      return '3ef0c088-6a96-4337-b8b6-6c6b1be61ab7'
    case 'fr':
      return '2ed915f6-1775-4108-928b-fb06cced312f'
    default:
      return 'b33577c1-33d6-4d47-8868-b47f5d63fa5c'
  }
})

const iconSize = computed(() => {
  return screenWidth.value > 720 ? 154 : 123
})

// Methods
async function onSubmitted(submittedValues) {
  emit('newsletter:subscribe', submittedValues.email.toLowerCase())
}

function onReady() {
  emit('newsletter:ready')
}
</script>

<style lang="postcss" scoped>
.newsletter {
  @apply relative p-6 flex flex-col w-full bg-grey-blue-100 rounded-[24px] shadow-light2;

  @screen md {
    @apply p-10;
  }

  .h2 {
    @apply font-medium text-xl leading-7;
    padding-right: 30%;

    @screen mdminus {
      @apply text-2xl leading-8;
    }

    @screen mdplus {
      @apply text-2xl leading-8;
    }
  }

  .icon {
    @apply absolute;
    right: 16px;
    top: -22px;

    @screen md {
      right: 44px;
      top: -29px;
    }

    @screen xl {
      right: 8px;
      top: -45px;
    }
  }

  &.in-content {
    @apply py-10 px-8 items-center;

    @screen md {
      padding-left: 71px;
      padding-right: 71px;
    }

    @screen xl {
      @apply px-12;
    }

    .tag {
      @apply self-center;
    }

    .h2 {
      @apply mt-6 p-0 text-center text-2xl leading-8;

      @screen md {
        max-width: 544px;
      }

      @screen mdminus {
        font-size: 28px;
        line-height: 34px;
      }

      @screen mdplus {
        @apply text-3xl leading-10;
      }

      @screen xl {
        @apply w-full;
      }
    }

    .icon {
      @apply hidden;

      @screen md {
        @apply flex;
      }
    }

    :deep(.native-form) {
      @screen md {
        max-width: 578px;
      }

      form {
        @apply mt-6;

        @screen md {
          @apply flex-wrap items-baseline mt-12;
        }

        .hs-email {
          @screen md {
            width: 430px !important;
          }
        }

        .hs-submit {
          @apply order-1;

          @screen md {
            @apply ml-2 flex-grow-0;
            width: 140px !important;
          }
        }

        .legal-consent-container {
          @apply order-2;

          @screen md {
            width: 508px;
          }

          @screen xl {
            @apply w-full;
          }
        }
      }
    }
  }

  :deep(.native-form) {
    @apply flex justify-center;

    form {
      @apply mt-4 flex flex-col w-full;

      @screen md {
        @apply mt-8 flex-row flex-wrap justify-center;
      }

      .hs-email {
        @apply w-full !important;

        label {
          @apply hidden;
        }

        .input {
          @apply mr-0 !important;
        }

        input {
          @apply w-full h-12 px-4 text-base rounded bg-white border border-grey-blue-300 text-winter-green-900 transition duration-300 ease-out !important;

          &.error {
            @apply border-red-600;
          }

          &:disabled {
            @apply bg-grey-blue-100 border-grey-blue-200;
          }

          &:focus {
            @apply border-livestorm-blue-700 outline-none;
          }

          &::placeholder {
            @apply text-grey-blue-600 text-base;
          }
        }
      }

      .hs-submit {
        @apply mt-4 flex flex-grow w-full order-1;

        .actions {
          @apply w-full;
        }
      }

      .no-list.hs-error-msgs {
        @apply hidden !important;

        .hs-error-msg {
          @apply hidden !important;
        }
      }

      .hs_error_rollup {
        @apply flex order-2 w-full mt-4;

        .hs-error-msgs {
          @apply flex w-full mt-0 py-4 !important;
        }
      }

      .legal-consent-container {
        @apply flex order-1 w-full mt-4;

        input {
          @apply mt-1;
        }

        span {
          @apply font-normal text-sm text-grey-blue-600 leading-6;

          a {
            @apply text-livestorm-blue-700;
          }
        }
      }
    }

    .submitted-message {
      @apply flex justify-center w-full mt-12 font-normal text-origin text-grey-blue-600 !important;
    }
  }
}
</style>
